import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Download, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { generateReportPDF } from '../lib/pdf';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ACCOUNTANT'];

export default function Attendance() {
  const { user } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [staff, setStaff] = useState([]);
  const [company, setCompany] = useState(null);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newRec, setNewRec] = useState({
    staff_id: '', staff_name: '',
    date: now.toISOString().split('T')[0],
    check_in: '08:00', check_out: '17:00',
    status: 'present', notes: ''
  });

  useEffect(() => { fetchData(); }, [month, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [a, r, s, c] = await Promise.all([
        axios.get(`${API}/attendance?month=${month}&year=${year}`, { withCredentials: true }),
        axios.get(`${API}/attendance/report?month=${month}&year=${year}`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setRecords(a.data); setSummary(r.data.summary || []); setStaff(s.data);
      setCompany(c.data.find(x => x.id === user?.company_id) || c.data[0]);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    const d = newRec.date;
    try {
      const ciStr = newRec.check_in ? `${d}T${newRec.check_in}:00Z` : null;
      const coStr = newRec.check_out ? `${d}T${newRec.check_out}:00Z` : null;
      await axios.post(`${API}/attendance`, {
        staff_id: newRec.staff_id,
        staff_name: newRec.staff_name,
        date: new Date(d).toISOString(),
        check_in: ciStr ? new Date(ciStr).toISOString() : null,
        check_out: coStr ? new Date(coStr).toISOString() : null,
        status: newRec.status,
        notes: newRec.notes
      }, { withCredentials: true });
      toast.success('Attendance logged');
      setShowNew(false);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const downloadPDF = () => {
    generateReportPDF({
      title: `Attendance Report — ${new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
      subtitle: `${records.length} records · ${summary.length} staff`,
      company,
      sections: [
        {
          heading: 'Staff Summary',
          head: ['Staff', 'Present', 'Absent', 'Leave', 'Half Day', 'Total Hours', 'Overtime'],
          rows: summary.map(s => [s.staff_name || s.staff_id, s.present || 0, s.absent || 0, s.leave || 0, s.half_day || 0, (s.total_hours || 0).toFixed(1), (s.overtime_hours || 0).toFixed(1)])
        },
        {
          heading: 'Detailed Records',
          head: ['Date', 'Staff', 'Status', 'Hours', 'Overtime'],
          rows: records.slice(0, 80).map(r => [
            new Date(r.date).toLocaleDateString(),
            r.staff_name || r.staff_id,
            r.status,
            (r.hours_worked || 0).toFixed(1),
            (r.overtime_hours || 0).toFixed(1)
          ])
        }
      ]
    });
  };

  return (
    <Layout>
      <div data-testid="attendance-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Attendance</h1>
            <p className="text-sm text-muted-foreground">Staff check-in/out, overtime and monthly reports</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger data-testid="month-select" className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>{[...Array(12)].map((_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="download-att-pdf" onClick={downloadPDF} variant="outline"><Download className="w-4 h-4 mr-1" />PDF</Button>
            {MANAGE.includes(user?.role) && (
              <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogTrigger asChild><Button data-testid="new-att-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />Log</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Log Attendance</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div><Label>Staff</Label>
                      <Select value={newRec.staff_id} onValueChange={(v) => {
                        const s = staff.find(x => x.id === v);
                        setNewRec({ ...newRec, staff_id: v, staff_name: s?.name || '' });
                      }}>
                        <SelectTrigger data-testid="att-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                        <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Date</Label><Input type="date" value={newRec.date} onChange={(e) => setNewRec({ ...newRec, date: e.target.value })} /></div>
                      <div><Label>Status</Label>
                        <Select value={newRec.status} onValueChange={(v) => setNewRec({ ...newRec, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present">Present</SelectItem>
                            <SelectItem value="absent">Absent</SelectItem>
                            <SelectItem value="leave">Leave</SelectItem>
                            <SelectItem value="half_day">Half Day</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Check-In</Label><Input type="time" value={newRec.check_in} onChange={(e) => setNewRec({ ...newRec, check_in: e.target.value })} /></div>
                      <div><Label>Check-Out</Label><Input type="time" value={newRec.check_out} onChange={(e) => setNewRec({ ...newRec, check_out: e.target.value })} /></div>
                    </div>
                    <Button data-testid="submit-att-btn" onClick={handleCreate} disabled={!newRec.staff_id} className="w-full bg-teal-600 hover:bg-teal-700">Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <Card className="p-5">
          <h3 className="font-semibold text-foreground mb-3">Monthly Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Staff</th>
                  <th className="px-3 py-2">Present</th>
                  <th className="px-3 py-2">Absent</th>
                  <th className="px-3 py-2">Leave</th>
                  <th className="px-3 py-2">Half-day</th>
                  <th className="px-3 py-2">Total Hrs</th>
                  <th className="px-3 py-2">Overtime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {summary.map((s, i) => (
                  <tr key={i} data-testid={`att-summary-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium">{s.staff_name || s.staff_id}</td>
                    <td className="px-3 py-2 text-center">{s.present || 0}</td>
                    <td className="px-3 py-2 text-center">{s.absent || 0}</td>
                    <td className="px-3 py-2 text-center">{s.leave || 0}</td>
                    <td className="px-3 py-2 text-center">{s.half_day || 0}</td>
                    <td className="px-3 py-2 text-center">{(s.total_hours || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 text-center font-semibold text-amber-600">{(s.overtime_hours || 0).toFixed(1)}</td>
                  </tr>
                ))}
                {summary.length === 0 && !loading && (
                  <tr><td colSpan="7" className="px-3 py-6 text-center text-muted-foreground"><ClipboardCheck className="w-8 h-8 mx-auto text-slate-300 mb-2" />No records for this month</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
